import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Select, Tag, Space, Input, message, Form, Button } from 'antd';
import { UserOutlined, BankOutlined, ReloadOutlined } from '@ant-design/icons';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import thLocale from '@fullcalendar/core/locales/th'; 
import withRoleAccess from '../../hoc/withRoleAccess';
import Sidebar from '../../components/Sidebar/Sidebar';
import Header from '../../components/Header/Header';
import moment from 'moment-timezone';
import TaskPopup from '../../components/Calendar/TaskPopup';
import { v4 as uuidv4 } from 'uuid';

import 'antd/dist/reset.css';
import './Classes.css';
import ClassDetailsModal from './ClassDetailsModal';

const { Option } = Select;

function AllClasses() {
  const { t, i18n } = useTranslation();
  const [classes, setClasses] = useState([]);
  const [teacherEvents, setTeacherEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState(null);
  const [selectedTeachers, setSelectedTeachers] = useState([]);
  const [selectedBranches, setSelectedBranches] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [, setClassrooms] = useState([]);
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const [calendarView, setCalendarView] = useState('dayGridMonth');
  const [calendarDate, setCalendarDate] = useState(new Date());
  const calendarRef = useRef(null);

  // Event modal states
  const [eventModalVisible, setEventModalVisible] = useState(false);
  const [eventForm] = Form.useForm();
  const [editingEvent, setEditingEvent] = useState(null);
  const [selectedEventType, setSelectedEventType] = useState('');
  const [selectedDateInfo, setSelectedDateInfo] = useState(null);


  const API_URL = process.env.REACT_APP_API_URL;

  // NEW: Function to get unified color scheme based on platform and branch
  const getUnifiedColorScheme = (platform, branchId) => {
    // Online classes - both branches
    if (platform === 'online') {
      return {
        backgroundColor: '#E6F4FF', // light beige
        borderColor: '#1890ff',      // blue
        textColor: '#1890ff',        // blue fonts
      };
    }
    
    // Onsite - branch_id 1
    if (platform === 'onsite' && branchId === 1) {
      return {
        backgroundColor: 'transparent', // No background
        borderColor: '#000000',          // black border
        textColor: '#000000',            // black fonts
      };
    }
    
    // Onsite - branch_id 2
    if (platform === 'onsite' && branchId === 2) {
      return {
        backgroundColor: '#FFFFE0',  // light yellow
        borderColor: '#DAA520',       // dark yellow/goldenrod
        textColor: '#000000',         // black fonts
      };
    }
    
    // Athome or other platforms - default styling
    return {
      backgroundColor: '#f0f0f0',
      borderColor: '#999999',
      textColor: '#333333',
    };
  };

  const refreshCalendarData = async () => {
    try {
      setLoading(true);
      
      // Store current calendar state
      const calendarApi = calendarRef.current?.getApi();
      const currentView = calendarApi?.view?.type;
      const currentDate = calendarApi?.getDate();
      
      
      // Fetch fresh data
      await Promise.all([
        fetchClasses(),
      ]);
      
      // Refresh teacher events if any teachers are selected
      if (selectedTeachers.length > 0) {
        await fetchTeacherEvents(selectedTeachers);
      }
      
      // Restore calendar view after data is loaded
      setTimeout(() => {
        if (calendarApi && currentView && currentDate) {
          calendarApi.changeView(currentView, currentDate);
          
          // Optionally refetch events if you're using FullCalendar's event sources
          calendarApi.refetchEvents();
        }
      }, 100);
      
      
    } catch (error) {
      console.error('❌ Error refreshing calendar data:', error);
      message.error('Failed to refresh calendar data');
    } finally {
      setLoading(false);
    }
  };

  const parseScheduleHour = (scheduleHourString) => {
    const [hours, minutes] = scheduleHourString.split(' ').map((value, index) => (index === 0 ? parseInt(value) * 60 : parseInt(value)));
    return hours + minutes;
  };

  const tagRender = (props) => {
    const { label, value, closable, onClose } = props;
    const teacher = teachers.find(t => t.userid === value);
    
    return (
      <Tag
        color={teacher?.color || '#108ee9'}
        closable={closable}
        onClose={onClose}
        style={{ 
          marginRight: 3,
          display: 'flex',
          alignItems: 'center',
          padding: '4px 8px',
        }}
      >
        <Space size={4}>
          <UserOutlined style={{ fontSize: '12px' }} />
          {label}
        </Space>
      </Tag>
    );
  };

  const branchTagRender = (props) => {
    const { label, closable, onClose } = props;
    
    return (
      <Tag
        color="#87d068"
        closable={closable}
        onClose={onClose}
        style={{ 
          marginRight: 3,
          display: 'flex',
          alignItems: 'center',
          padding: '4px 8px',
        }}
      >
        <Space size={4}>
          <BankOutlined style={{ fontSize: '12px' }} />
          {label}
        </Space>
      </Tag>
    );
  };

  const fetchClasses = useCallback(
    async (viewDate = new Date()) => {
      setLoading(true);
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          setError("No token found");
          setLoading(false);
          return;
        }

        const response = await axios.get(`${API_URL}/classes/calendar-classes`, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            viewDate: viewDate.toISOString().split("T")[0], // YYYY-MM-DD
          },
        });

        // ✅ Use response.data.data
        setClasses(response.data?.data || []);
      } catch (error) {
        console.error("❌ Error fetching classes:", error);
        setError("Failed to fetch classes. Please ensure you are logged in.");
      } finally {
        setLoading(false);
      }
    },
    [API_URL]
  );

 
const fetchTeacherEvents = useCallback(async (teacherIds) => {
  if (teacherIds.length === 0) {
    setTeacherEvents([]);
    return;
  }

  try {
    const token = localStorage.getItem("token");

    // Fetch all teachers' events in parallel
    const promises = teacherIds.map((teacherId) =>
      axios
        .get(`${API_URL}/teachers/calendar/${teacherId}/events-with-shared`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((response) => ({
          teacherId,
          data: response.data,
        }))
    );

    const results = await Promise.all(promises);

    // Helper for consistent styling
    const getEventStyling = (eventType, isShared, teacher) => {
      if (eventType === "class_cancelled") {
        return {
          backgroundColor: "transparent",
          borderColor: "#dc3545",
          textColor: "#dc3545",
        };
      }

      if (eventType === "class_reserved") {
        return {
          backgroundColor: "transparent",
          borderColor: "#1890ff",
          textColor: "#1890ff",
        };
      }

      // Shared event styling
      if (isShared) {
        return {
          backgroundColor: "#00508260",
          borderColor: "#005082",
          textColor: "#ffffff",
        };
      }

      // Teacher-specific styling for teacher events (not classes)
      const teacherColor = teacher?.color || "#444444";
      return {
        backgroundColor: `${teacherColor}60`,
        borderColor: teacherColor,
        textColor: "#ffffff",
      };
    };

    // Clean + normalize events
    const formattedEvents = results.flatMap((result) =>
      result.data.map((event) => {
        const teacher = teachers.find((t) => t.userid === result.teacherId);
        const isShared = event.is_shared || false;
        const eventType = event.event_type || "regular";

        // 🔥 Clean eventid right here (NO prefix allowed)
        const cleanEventId = event.eventid
          ?.toString()
          .replace(/^\D+/g, "") // remove event-, task-, class-, any non-digits
          .trim();

        // 🔥 Safe styling
        const styling = getEventStyling(eventType, isShared, teacher);

        return {
          // Base event data (safe)
          eventid: cleanEventId, // 🔥 ensures PUT/DELETE always work
          id: `event-${cleanEventId}`, // FullCalendar unique ID
          title: event.title,
          description: event.description,
          video_call_link: event.video_call_link,
          recurrence: event.recurrence,
          groupid: event.groupid,

          start: moment(event.start).format(),
          end: moment(event.end).format(),

          // Calendar visible styling
          backgroundColor: styling.backgroundColor,
          borderColor: styling.borderColor,
          textColor: styling.textColor,
          color: styling.borderColor, // needed for some FC views

          classNames: ["teacher-schedule-event"],

          extendedProps: {
            event_type: eventType,
            is_shared: isShared,
            teacher_id: result.teacherId,
            sharedUsers: event.shared_users || [],
            sharedRoles: event.shared_roles || [],
            sharedAll: event.shared_all || false,
            description: event.description,
            video_call_link: event.video_call_link,
            groupid: event.groupid,
          },
        };
      })
    );

    setTeacherEvents(formattedEvents);
  } catch (error) {
    console.error("Error fetching teacher events:", error);
    setError("Failed to fetch events. Please try again later.");
    message.error("Failed to load teacher events");
  }
}, [API_URL, teachers]);



  useEffect(() => {
    fetchClasses();
    setTeacherEvents([]); 
  }, [fetchClasses, fetchTeacherEvents]);
  
  useEffect(() => {
    const fetchTeachers = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/teachers`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const teachersWithColors = (response.data.teachers || []).map((teacher) => ({
          ...teacher,
          color: teacher.color || '#000000',
        }));

        setTeachers(teachersWithColors);

      } catch (error) {
        console.error('Error fetching teachers:', error);
      }
    };

    fetchTeachers();
  }, [API_URL]);

  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/branch`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        setBranches(response.data || []);

      } catch (error) {
        console.error('Error fetching branches:', error);
      }
    };

    fetchBranches();
  }, [API_URL]);

  useEffect(() => {
    const fetchClassrooms = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/classrooms`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
  
        const classroomsData = response.data || [];
  
        // Filter classrooms with availability 'true'
        const availableClassrooms = classroomsData.filter(classroom => classroom.availability === true);
  
        // Sort by 'number' field in alphanumeric order
        const sortedClassrooms = availableClassrooms.sort((a, b) => {
          return a.number.localeCompare(b.number, undefined, { numeric: true, sensitivity: 'base' });
        });
  
        setClassrooms(sortedClassrooms);
      } catch (error) {
        console.error('Error fetching classrooms:', error);
      }
    };
  
    fetchClassrooms();
  }, [API_URL]);
  
  useEffect(() => {
    if (selectedTeachers.length > 0) {
      fetchTeacherEvents(selectedTeachers);
    } else {
      setTeacherEvents([]);
    }
  }, [selectedTeachers, fetchTeacherEvents]);

  const handleEventClick = (info) => {
    const eventId = info.event.id;
    
    // For teacher events
    if (eventId.includes('event-')) {
      const eventIdNum = eventId.replace('event-', '');
      const teacherEvent = teacherEvents.find(evt => evt.eventid?.toString() === eventIdNum);
      if (teacherEvent) {
        // Open edit modal for teacher event
        openEventModal(teacherEvent);
      }
    } else {
      // For class events
      const classItem = classes.find(cls => cls.classid.toString() === eventId);
      if (classItem) {
        const teacher = teachers.find(t => t.userid === classItem.teacher_id);
        setSelectedClass({
          classData: classItem,
          teacherData: teacher
        });
        setDetailsModalVisible(true);
      }
    }
  };

const handleDateSelect = (selectInfo) => {
  setSelectedDateInfo(selectInfo);
  openEventModal(null, selectInfo);
};


  // Open event modal for create/edit
  const openEventModal = (event = null, selectInfo = null) => {
    if (event) {
      // Editing existing event
      setEditingEvent(event);
      setSelectedEventType(event.event_type || 'regular');
      eventForm.setFieldsValue({
        title: event.title,
        description: event.description,
        date: moment(event.start),
        startTime: moment(event.start),
        endTime: moment(event.end),
        event_type: event.event_type || 'regular',
        video_call_link: event.video_call_link,
        groupId: event.groupid || uuidv4()
      });
    } else if (selectInfo) {
      // Creating new event from date selection
      setEditingEvent(null);
      setSelectedEventType('regular');
      eventForm.setFieldsValue({
        date: moment(selectInfo.start),
        startTime: moment(selectInfo.start),
        endTime: moment(selectInfo.end || selectInfo.start).add(1, 'hour'),
        event_type: 'regular',
        groupId: uuidv4()
      });
    } else {
      // Creating new event from button
      setEditingEvent(null);
      setSelectedEventType('regular');
      eventForm.resetFields();
    }
    setEventModalVisible(true);
  };
  
  
  const filteredClasses = classes
    .filter((classItem) => {
      const isTeacherMatch =
        selectedTeachers.length > 0
          ? selectedTeachers.includes(classItem.teacher_id)
          : true;
      
      const isBranchMatch =
        selectedBranches.length > 0
          ? selectedBranches.includes(classItem.branch_id)
          : true;
      
      const isSearchMatch = searchTerm === '' || 
        classItem.class_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        classItem.subject_name?.toLowerCase().includes(searchTerm.toLowerCase());
      
      return isTeacherMatch && isBranchMatch && isSearchMatch;
    });

  const formatClassToEvent = (classItem) => {
    const startDate = new Date(classItem.date);
    const [startHours, startMinutes] = classItem.schedule_time.split(':').map(Number);

    if (isNaN(startHours) || isNaN(startMinutes)) {
        console.error('Invalid schedule time format:', classItem.schedule_time);
        return null;
    }

    startDate.setHours(startHours);
    startDate.setMinutes(startMinutes);
    startDate.setSeconds(0);
    startDate.setMilliseconds(0);

    if (isNaN(startDate.getTime())) {
        console.error('Invalid start date or time:', classItem.date, classItem.schedule_time, startDate);
        return null;
    }

    const totalMinutes = parseScheduleHour(classItem.schedule_hour || '');
    const endDateTime = new Date(startDate.getTime() + totalMinutes * 60000);

    if (isNaN(endDateTime.getTime())) {
        console.error('Invalid end date or time:', startDate, endDateTime, classItem.schedule_hour);
        return null;
    }

    // Get platform icon and text
    const getPlatformIcon = (platform) => {
      switch(platform) {
        case 'online':
          return `💻 ${t('online')}`;
        case 'onsite':
          return `📍 ${t('onsite')}`;
        case 'athome':
          return `🏠 ${t('athome')}`;
        default:
          return `❓ ${platform}`;
      }
    };

    const getClassTypeLabel = (isPrivate) => {
      return isPrivate ? `👤 ${t('private')}` : `👥 ${t('group')}`;
    };

    // Course only — the subject/class line and the teacher nickname are intentionally omitted
    const titleLines = [
      `${classItem.class_code}`,
    ];

    // Determine event styling based on event_type
    const eventType = classItem.event_type || 'regular';
    let backgroundColor, borderColor, textColor;

    if (eventType === 'class_cancelled') {
      // Cancelled: transparent background, red border, red text
      backgroundColor = 'transparent';
      borderColor = '#dc3545'; // Red
      textColor = '#dc3545';
    } else if (eventType === 'class_reserved') {
      // Reserved: transparent background, blue border, blue text
      backgroundColor = 'transparent';
      borderColor = '#1890ff'; // Blue
      textColor = '#1890ff';
    } else {
      // NEW: Use unified color scheme based on platform and branch_id
      const colorScheme = getUnifiedColorScheme(classItem.platform, classItem.branch_id);
      backgroundColor = colorScheme.backgroundColor;
      borderColor = colorScheme.borderColor;
      textColor = colorScheme.textColor;
    }

    // Attendance-based override (wins over platform/branch styling).
    // Priority: Absent > To be rescheduled. Both signal a problem; Absent is solid red, rescheduled is outlined red.
    const attRecords = classItem.attendance_statuses || [];
    const hasAbsent = attRecords.some((a) => a.status === 'Absent');
    const hasToBeRescheduled = attRecords.some((a) => a.status === 'To be rescheduled');
    if (hasAbsent) {
      backgroundColor = '#ff4d4f';
      borderColor = '#ff4d4f';
      textColor = '#ffffff';
    } else if (hasToBeRescheduled) {
      backgroundColor = '#ffffff';
      borderColor = '#ff4d4f';
      textColor = '#cf1322';
    }

    return {
        id: classItem.classid.toString(),  
        title: titleLines.join('<br />'), 
        start: startDate.toISOString(), 
        end: endDateTime.toISOString(),
        color: borderColor,
        backgroundColor: backgroundColor,
        borderColor: borderColor,
        textColor: textColor,
        extendedProps: {
          platform: getPlatformIcon(classItem.platform),
          classType: getClassTypeLabel(classItem.isprivate),
          branchName: classItem.name || 'Unknown Branch',
          student_confirm: classItem.student_confirm === true || classItem.student_confirm === 'true',
          event_type: eventType,
          attendance_statuses: classItem.attendance_statuses || [],
        }
    };
  };

  const renderEventContent = (arg) => {
    const event = arg.event;
    const startTime = event.start ? moment(event.start).format('HH:mm') : '';
    const endTime = event.end ? moment(event.end).format('HH:mm') : '';
    const platform = event.extendedProps?.platform || '';
    const branchName = event.extendedProps?.branchName || '';
    const confirmed = event.extendedProps?.student_confirm;
    const eventType = event.extendedProps?.event_type || 'regular';
    const isTeacherEvent = event.id.includes('event-');

    // Choose icon based on confirmed state
    const confirmIcon = confirmed ? `✅` : `❌`;

    // Determine styling based on event type
    let containerStyle = `
      padding: 4px;
      border-radius: 4px;
      min-height: 100%;
      font-size: 1.05em;
      position: relative;
      border: 2px solid ${event.borderColor};
    `;

    let textStyle = `color: ${event.textColor || '#333'};`;

    if (eventType === 'class_cancelled') {
      containerStyle += `background: transparent !important;`;
    } else if (eventType === 'class_reserved') {
      containerStyle += `background: transparent !important;`;
    } else {
      containerStyle += `background: ${event.backgroundColor};`;
    }

    // For teacher events, show simpler layout
    if (isTeacherEvent) {
      return {
        html: `
         <div class="calendar-event teacher-event" style="${containerStyle}">
            <div style="margin-bottom: 2px; display: flex; align-items: center; gap: 6px; flex-wrap: wrap; font-size: 1.35em; font-weight: 700; line-height: 1.2; ${textStyle}">
              <span>${startTime} - ${endTime}</span>
            </div>
            <div style="${textStyle}">
              ${event.title}
            </div>
          </div>
        `,
      };
    }

    // Attendance badges: spell out status (Present/Absent/Rescheduled/Late)
    const attendanceRecords = event.extendedProps?.attendance_statuses || [];
    const counts = attendanceRecords.reduce((acc, r) => {
      acc[r.status] = (acc[r.status] || 0) + 1;
      return acc;
    }, {});
    const badge = (label, count, bg, color, border) => count > 0 ? `
      <span style="
        display: inline-flex;
        align-items: center;
        justify-content: center;
        height: 16px;
        padding: 0 6px;
        margin-left: 2px;
        border-radius: 8px;
        background: ${bg};
        color: ${color};
        ${border ? `border: 1px solid ${border};` : ''}
        font-size: 10px;
        font-weight: 700;
        line-height: 1;
        white-space: nowrap;
      ">${label}${count > 1 ? ` ${count}` : ''}</span>` : '';
    const attendanceBadges = [
      badge('Present', counts['Present'] || 0, '#52c41a', '#ffffff'),
      badge('Absent', counts['Absent'] || 0, '#ffffff', '#cf1322', '#ff4d4f'),
      badge('TBR', counts['To be rescheduled'] || 0, '#ff4d4f', '#ffffff'),
      badge('Late', counts['Late'] || 0, '#faad14', '#ffffff'),
    ].join('');
    const badgeRow = attendanceBadges ? `
      <div style="
        position: absolute;
        bottom: 2px;
        right: 2px;
        display: flex;
        flex-wrap: wrap;
        justify-content: flex-end;
        gap: 2px;
        max-width: 85%;
      ">${attendanceBadges}</div>` : '';

    // For class events, show full details
    return {
      html: `
       <div class="calendar-event teacher-event" style="${containerStyle}">
          <div style="margin-bottom: 2px; display: flex; align-items: center; gap: 6px; flex-wrap: wrap; font-size: 1.35em; font-weight: 700; line-height: 1.2; ${textStyle}">
            <span>${startTime} - ${endTime} ${confirmIcon}</span>
          </div>
          <div style="margin-bottom: 20px; ${textStyle}">
            ${event.title}
          </div>
          <div class="platform-tag" style="
            position: absolute;
            bottom: 2px;
            left: 2px;
            background: rgb(255, 255, 255);
            padding: 2px 4px;
            border-radius: 3px;
            font-size: 0.9em;
            color: #333;
            box-shadow: 0 1px 2px rgba(0,0,0,0.1);
          ">
            ${platform} 🏢 ${branchName}
          </div>
          ${badgeRow}
        </div>
      `,
    };
  };

const handleRefresh = async () => {
  try {
    setLoading(true);
    await fetchClasses();
    
    // Also refresh teacher events if any teachers are selected
    if (selectedTeachers.length > 0) {
      await fetchTeacherEvents(selectedTeachers);
    } else {
      setTeacherEvents([]);
    }
    
    message.success('Data refreshed successfully');
  } catch (error) {
    message.error('Failed to refresh data');
  } finally {
    setLoading(false);
  }
};

  const calendarEvents = [
    ...filteredClasses.map(formatClassToEvent).filter((event) => event !== null),
    ...teacherEvents // Use directly since they're already formatted
  ];

  if (loading) return <p>Loading...</p>;
  if (error) return <p>{error}</p>;

  return (
    <div className="class-container">
      <Sidebar />
      <Header />
      <div className="top-filter-container">
        <div className="teacher-select-container">
          <div>
            <Input.Search
              placeholder={t('Search Course or Subject Name')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: '350px' }}
              allowClear
            />
          </div>
          
          <Select
            mode="multiple"
            showSearch
            style={{ width: '100%' }}
            placeholder={t('Select Teachers')}
            value={selectedTeachers}
            onChange={setSelectedTeachers}
            optionFilterProp="label"
            tagRender={tagRender}
            maxTagCount={3}
            maxTagPlaceholder={(omittedValues) => `+ ${omittedValues.length} ${t('more teachers')}`}
            filterOption={(input, option) => {
              return option?.label?.toLowerCase().includes(input.toLowerCase());
            }}
            dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
          >
            {teachers.map(teacher => (
              <Option 
                key={teacher.userid} 
                value={teacher.userid}
                label={`${teacher.nickname}`}
                disabled={!selectedTeachers.includes(teacher.userid) && selectedTeachers.length >= 5}
                style={{
                  padding: '8px 12px',
                  display: 'flex',
                  alignItems: 'center',
                  opacity: (!selectedTeachers.includes(teacher.userid) && selectedTeachers.length >= 5) ? 0.5 : 1
                }}
              >
                <Space>
                  <Tag color={teacher.color} style={{ margin: 0, width: 8, height: 8 }} />
                  <span>{teacher.nickname}</span>
                </Space>
              </Option>
            ))}
          </Select>

          <Select
            mode="multiple"
            showSearch
            style={{ width: '100%' }}
            placeholder={t('Select Branches')}
            value={selectedBranches}
            onChange={setSelectedBranches}
            optionFilterProp="label"
            tagRender={branchTagRender}
            maxTagCount={2}
            maxTagPlaceholder={(omittedValues) => `+ ${omittedValues.length} ${t('more branches')}`}
            filterOption={(input, option) => {
              return option?.label?.toLowerCase().includes(input.toLowerCase());
            }}
            dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
            notFoundContent={branches.length === 0 ? "Loading branches..." : "No branches found"}
          >
            {branches.map(branch => (
              <Option 
                key={branch.id} 
                value={branch.id}
                label={branch.name}
                style={{
                  padding: '8px 12px',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Space>
                  <BankOutlined style={{ fontSize: '12px' }} />
                  <span>{branch.name}</span>
                </Space>
              </Option>
            ))}
          </Select>

          <Button
            type="primary"
          icon={<ReloadOutlined />}
          onClick={handleRefresh}
          loading={loading}
          style={{ marginLeft: '10px' }}
        >
          {t('Refresh')}
        </Button>
        </div>
      </div>

      <div className="class-calendar-container allclasses-calendar">
        {selectedTeachers.length === 0 ? (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '400px',
            flexDirection: 'column',
            color: '#999',
            fontSize: '16px'
          }}>
            <div style={{ marginBottom: '10px' }}>📅</div>
            <div>{t('Please select at least one teacher to view the calendar')}</div>
          </div>
        ) : (
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            initialView={calendarView}
            initialDate={calendarDate}
            allDaySlot={false}
            locale={i18n.language === 'th' ? thLocale : 'en'}
            headerToolbar={{
              left: 'prev,next today',
              center: 'title',
              right: 'dayGridMonth, timeGridDay',
            }}
            slotMinTime="09:00:00"
            slotMaxTime="20:00:00"
            slotDuration="00:15:00" 
            snapDuration="00:15:00" 
            slotLabelInterval="00:30" 
            slotLabelFormat={{
              hour: '2-digit',
              minute: '2-digit',
              hour12: false, 
            }}
            events={calendarEvents}
            editable={false} // Disabled drag and drop
            droppable={false} // Disabled drag and drop
            selectable={true}
            selectMirror={true}
            select={handleDateSelect}
            eventClick={handleEventClick}
            eventDisplay="block"
            eventBackgroundColor="transparent"
            eventContent={renderEventContent}
            eventTextColor={null}
            eventColor={null}
            eventClassNames={(info) => info.event.extendedProps.classNames}
            dayMaxEvents={0}
            height="100%"
            datesSet={(dateInfo) => {
              const viewType = dateInfo.view.type;

              // Always use the focal date, not the first of the month
              const targetDate = dateInfo.view.calendar.getDate();

              const targetDateStr = targetDate.toLocaleDateString();
              const currentDateStr = calendarDate ? calendarDate.toLocaleDateString() : '';

              if (targetDateStr !== currentDateStr || calendarView !== viewType) {
                setCalendarDate(targetDate);
                setCalendarView(viewType);
                
                // Only fetch classes if teachers are selected
                if (selectedTeachers.length > 0) {
                  fetchClasses(targetDate, selectedTeachers);
                }
              }
            }}
            views={{
              dayGridMonth: {
                displayEventTime: true,
                displayEventEnd: true,
                dayMaxEvents: false
              },
              timeGridDay: {
                displayEventTime: true,
                displayEventEnd: true
              }
            }}
          />
        )}
      </div>

      <ClassDetailsModal
        visible={detailsModalVisible}
        onClose={() => {
          setDetailsModalVisible(false);
          setSelectedClass(null);
        }}
        classData={selectedClass?.classData}
        teacherData={selectedClass?.teacherData}
        onRefresh={refreshCalendarData}
      />

      <TaskPopup
        isOpen={eventModalVisible}
        task={editingEvent}
        onClose={() => {
          setEventModalVisible(false);
          setEditingEvent(null);
        }}
        onSave={refreshCalendarData}
        date={selectedDateInfo}
        tasks={teacherEvents}
        classes={classes}
        users={teachers}
        selectedTeachers={selectedTeachers}  // 👈 Add this prop
      />

    </div>
  );
}

export default withRoleAccess(AllClasses, ['admin', 'superadmin']);